import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from 'react-toastify';
import {
  fetchMentorHierarchy,
  updateMentorHierarchy,
  unlockMentorHierarchy,
} from '../../../api/mentorApi';
import HierarchyTree from './HierarchyTree';
import HierarchyChart from './HierarchyChart';
import EditHierarchyModal from './EditHierarchyModal';
import { flattenTree, formatMentorName } from './hierarchyUtils';

const VIEW_MODE_KEY = 'hierarchyViewMode';

const HierarchySection = () => {
  const [tree, setTree] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState('');

  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) || 'tree';
    } catch {
      return 'tree';
    }
  });

  const changeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      /* storage unavailable — the toggle still works for this session */
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMentorHierarchy();
      setTree(data.tree || []);
      setOrphans(data.orphans || []);
    } catch (err) {
      console.error('Error loading mentor hierarchy:', err);
      setError('Failed to load the mentor hierarchy. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Flat lookup for the modal's supervisor dropdown.
  const allMentors = useMemo(
    () => [...flattenTree(tree), ...flattenTree(orphans)],
    [tree, orphans]
  );

  // Links that predate this screen can break the rank rule. Surface the count
  // so an admin can find and fix them instead of discovering them by chance.
  const violationCount = useMemo(
    () => allMentors.filter((m) => m.rankViolation).length,
    [allMentors]
  );

  const matchesSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return (node) => formatMentorName(node).toLowerCase().includes(q);
  }, [search]);

  // The tree is reloaded from the server after every write rather than patched
  // locally — the server is the authority on the resulting shape.
  const handleSave = async (mentorId, changes) => {
    try {
      setSaving(true);
      await updateMentorHierarchy(mentorId, changes);
      toast.success('Hierarchy updated');
      setEditTarget(null);
      await load();
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update hierarchy. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (mentorId) => {
    try {
      setSaving(true);
      await unlockMentorHierarchy(mentorId);
      toast.success('Unlocked — the sync will manage this mentor again');
      setEditTarget(null);
      await load();
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to unlock. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hierarchy-section">
      <div className="hierarchy-toolbar">
        <div className="hierarchy-toolbar-left">
          <input
            type="text"
            className="hierarchy-search"
            placeholder="Search mentors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="hierarchy-refresh" onClick={load} disabled={loading}>
            <i className="bi bi-arrow-clockwise" /> Refresh
          </button>
        </div>

        <div className="hierarchy-viewtoggle" role="group" aria-label="View mode">
          <button
            type="button"
            className={viewMode === 'tree' ? 'active' : ''}
            onClick={() => changeViewMode('tree')}
          >
            <i className="bi bi-list-nested" /> Tree
          </button>
          <button
            type="button"
            className={viewMode === 'chart' ? 'active' : ''}
            onClick={() => changeViewMode('chart')}
          >
            <i className="bi bi-diagram-3" /> Org chart
          </button>
        </div>
      </div>

      {loading && (
        <div className="hierarchy-skeleton">
          <Skeleton count={8} height={38} style={{ marginBottom: '0.5rem' }} />
        </div>
      )}

      {!loading && error && (
        <div className="hierarchy-error">
          {error}
          <button type="button" onClick={load}>Retry</button>
        </div>
      )}

      {!loading && !error && violationCount > 0 && (
        <div className="hierarchy-violation-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {violationCount} mentor{violationCount === 1 ? ' has a supervisor who does' : 's have supervisors who do'}{' '}
          not outrank them. Look for the warning marker and edit to fix.
        </div>
      )}

      {!loading && !error && viewMode === 'tree' && (
        <HierarchyTree
          tree={tree}
          orphans={orphans}
          onEdit={setEditTarget}
          matchesSearch={matchesSearch}
        />
      )}

      {!loading && !error && viewMode === 'chart' && (
        <HierarchyChart tree={tree} orphans={orphans} onEdit={setEditTarget} />
      )}

      {editTarget && (
        <EditHierarchyModal
          mentor={editTarget}
          allMentors={allMentors}
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
          onUnlock={handleUnlock}
          saving={saving}
        />
      )}
    </div>
  );
};

export default HierarchySection;
